import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import './LiveSiteViewer.css';

const LiveSiteViewer = ({ url, title }) => {
    const [device, setDevice] = useState('desktop');
    const [isLoading, setIsLoading] = useState(true);
    const [isFullscreen, setIsFullscreen] = useState(false);
    const [isMinimized, setIsMinimized] = useState(false);
    const [isClosed, setIsClosed] = useState(false);

    useEffect(() => {
        const handleEsc = (e) => {
            if (e.key === 'Escape') setIsFullscreen(false);
        };

        if (isFullscreen) {
            window.addEventListener('keydown', handleEsc);
            document.body.style.overflow = 'hidden';
        } else {
            document.body.style.overflow = '';
        }

        return () => {
            window.removeEventListener('keydown', handleEsc);
            document.body.style.overflow = '';
        };
    }, [isFullscreen]);

    if (!url) return null;

    // Ensure URL has protocol
    const safeUrl = url.startsWith('http') ? url : `https://${url}`;
    const displayUrl = safeUrl.replace(/^https?:\/\//, '');

    const handleClose = () => {
        if (isFullscreen) {
            setIsFullscreen(false);
        } else {
            setIsClosed(true);
        }
    };

    // Le bouton Jaune gère maintenant le Plein Écran (Toggle)
    const handleYellowClick = () => {
        setIsFullscreen(!isFullscreen);
    };

    if (isClosed) {
        return (
            <div className="live-site-viewer-placeholder">
                <button className="reopen-viewer-btn" onClick={() => setIsClosed(false)}>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"></circle><polygon points="10 8 16 12 10 16 10 8"></polygon></svg>
                    Voir l'aperçu du site
                </button>
            </div>
        );
    }

    const viewerContent = (
        <div className={`live-site-viewer-container ${isFullscreen ? 'fullscreen-mode' : ''} ${isMinimized ? 'minimized' : ''}`}>
            <div className="viewer-header" onDoubleClick={handleYellowClick}>
                <div className="viewer-actions-left">
                    <div className="window-dots">
                        {/* ROUGE : Fermer */}
                        <button className="dot red" onClick={handleClose} aria-label="Fermer" title="Fermer le lecteur">
                            <svg viewBox="0 0 10 10" width="6" height="6"><path d="M1 1 L9 9 M9 1 L1 9" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" /></svg>
                        </button>

                        {/* JAUNE : Plein écran (Toggle) */}
                        <button className="dot yellow" onClick={handleYellowClick} aria-label={isFullscreen ? "Quitter plein écran" : "Plein écran"} title={isFullscreen ? "Réduire (Quitter plein écran)" : "Agrandir (Plein écran)"}>
                            <svg viewBox="0 0 10 10" width="6" height="6">
                                {isFullscreen ? (
                                    /* Icone de réduction (flèches vers l'intérieur) */
                                    <path d="M1 9 L9 1 M9 1 L4 1 M9 1 L9 6" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" fill="none" />
                                ) : (
                                    /* Icone d'agrandissement (flèches vers l'extérieur) */
                                    <path d="M1 9 L9 1 M1 9 L1 4 M1 9 L6 9" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" fill="none" />
                                )}
                            </svg>
                        </button>

                        {/* VERT : Ouvrir le site en Plein Écran */}
                        <button className="dot green" onClick={() => setIsFullscreen(!isFullscreen)} aria-label={isFullscreen ? "Quitter plein écran" : "Plein écran"} title={isFullscreen ? "Réduire (Quitter plein écran)" : "Agrandir (Plein écran)"}>
                            <svg viewBox="0 0 10 10" width="6" height="6">
                                {isFullscreen ? (
                                    <path d="M1 9 L9 1 M9 1 L4 1 M9 1 L9 6" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" fill="none" />
                                ) : (
                                    <path d="M1 9 L9 1 M1 9 L1 4 M1 9 L6 9" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" fill="none" />
                                )}
                            </svg>
                        </button>
                    </div>
                </div>

                <div className="address-bar">
                    <svg className="lock-icon" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <rect x="3" y="11" width="18" height="11" rx="2" ry="2"></rect>
                        <path d="M7 11V7a5 5 0 0 1 10 0v4"></path>
                    </svg>
                    <a href={safeUrl} target="_blank" rel="noopener noreferrer" className="address-link" title={`Visiter ${displayUrl}`}>
                        {displayUrl}
                        <svg className="url-external-icon" width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"></path>
                            <polyline points="15 3 21 3 21 9"></polyline>
                            <line x1="10" y1="14" x2="21" y2="3"></line>
                        </svg>
                    </a>
                </div>

                <div className="viewer-actions-right">
                    <button
                        className={`device-btn ${isFullscreen ? 'active' : ''}`}
                        onClick={handleYellowClick}
                        title={isFullscreen ? "Quitter le plein écran" : "Plein écran"}
                    >
                        {isFullscreen ? (
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M8 3v3a2 2 0 0 1-2 2H3m18 0h-3a2 2 0 0 1-2-2V3m0 18v-3a2 2 0 0 1 2-2h3M3 16h3a2 2 0 0 1 2 2v3"></path>
                            </svg>
                        ) : (
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M15 3h6v6M9 21H3v-6M21 3l-7 7M3 21l7-7"></path>
                            </svg>
                        )}
                    </button>


                    <div className="device-separator" style={{ width: '1px', height: '16px', background: 'rgba(255,255,255,0.1)', margin: '0 4px' }}></div>

                    <div className="device-toggles">
                        <button
                            className={`device-btn ${device === 'desktop' ? 'active' : ''}`}
                            onClick={() => { setDevice('desktop'); if (isMinimized) setIsMinimized(false); }}
                            title="Vue Ordinateur"
                        >
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="2" y="3" width="20" height="14" rx="2" ry="2"></rect><line x1="8" y1="21" x2="16" y2="21"></line><line x1="12" y1="17" x2="12" y2="21"></line></svg>
                        </button>
                        <button
                            className={`device-btn ${device === 'mobile' ? 'active' : ''}`}
                            onClick={() => { setDevice('mobile'); if (isMinimized) setIsMinimized(false); }}
                            title="Vue Mobile"
                        >
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="5" y="2" width="14" height="20" rx="2" ry="2"></rect><line x1="12" y1="18" x2="12" y2="18"></line></svg>
                        </button>
                    </div>
                    <button
                        className="device-btn"
                        onClick={handleYellowClick}
                        title={isFullscreen ? "Quitter le plein écran" : "Plein écran"}
                    >
                        {isFullscreen ? (
                            /* Croix pour FERMER le plein écran */
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <line x1="18" y1="6" x2="6" y2="18"></line>
                                <line x1="6" y1="6" x2="18" y2="18"></line>
                            </svg>
                        ) : (
                            /* Icone OUVRIR (style lien externe) */
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"></path>
                                <polyline points="15 3 21 3 21 9"></polyline>
                                <line x1="10" y1="14" x2="21" y2="3"></line>
                            </svg>
                        )}
                    </button>
                </div>
            </div>

            <div className={`viewer-viewport-wrapper ${device}`}>
                <div className="viewer-viewport">
                    {isLoading && (
                        <div className="viewer-loader">
                            <div className="spinner"></div>
                            <span>Chargement du site...</span>
                        </div>
                    )}
                    <iframe
                        src={safeUrl}
                        title={`Aperçu de ${title || 'site'}`}
                        className="viewer-iframe"
                        onLoad={() => setIsLoading(false)}
                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                        sandbox="allow-same-origin allow-scripts allow-forms allow-popups"
                    />
                </div>
            </div>
        </div>
    );

    // Use Portal when fullscreen to escape any stacking context (e.g., from filters or transforms in parent)
    return isFullscreen ? createPortal(viewerContent, document.body) : viewerContent;
};

export default LiveSiteViewer;
